<script>
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {useEvaluationFileStore} from "@/stores/Evaluation/EvaluationFile";
import {computed, onMounted, ref} from "vue";
import useSharedSelectData from "@/use/SharedSelectData";
import axios from "axios";
import QuestionForm from "@/views/Evaluation/File/Components/QuestionForm.vue";
import CriteriaForm from "@/views/Evaluation/File/Components/CriteriaForm.vue";
import WCodeGeneration from "@/components/WCodeGeneration.vue";

export default {
    components: {WCodeGeneration, CriteriaForm, QuestionForm},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
        duplicateAction: Boolean,
        archiveAction: Boolean,
        unarchiveAction: Boolean,
        type: String,
        externalRootPath: String,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction || props.archiveAction || props.unarchiveAction);
        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {evaluationCycles} = useSharedSelectData();
        const {getBack} = useSharedMethod();
        const {$refs, $route} = useTools();
        
        const evaluationFileStore = useEvaluationFileStore();
        
        
        const evaluationFile = ref({
            preSelectedNote: false,
            activateTarget: false,
            activateCategoryGravity: false,
            activateCriterionGravity: false,
            isPercent: false,
            archive: false,
            occupationIds: [],
            socioProfessionalIds: [],
            steps: [],
            categories: [],
            questions: []
        });
        const categories = ref([]);
        const questions = ref([]);
        
        function getDefaultValues() {
            loading.value = true;
            axios.get(`/evaluationFiles/defaultValues/${props.type}`).then(response => {
                evaluationFile.value.preSelectedNote = response.data.preSelectedNote;
                evaluationFile.value.activateTarget = response.data.activateTarget;
                evaluationFile.value.activateCategoryGravity = response.data.activateCategoryGravity;
                evaluationFile.value.activateCriterionGravity = response.data.activateCriterionGravity;
                evaluationFile.value.isPercent = response.data.isPercent;
                evaluationFile.value.gateway = response.data.gateway;
            }).finally(() => {
                loading.value = false;
            });
        }
        
        const occupationId = ref(null);
        const occupations = ref([]);
        const loadingOccupations = ref(false);
        
        function findNotAssignedOccupation() {
            loadingOccupations.value = true;
            axios.get(`/evaluationFiles/findNotAssignedOccupation`, {
                params: {
                    id: $route.params.id,
                    type: props.type
                }
            }).then(response => {
                occupations.value = response.data;
            }).finally(() => {
                loadingOccupations.value = false;
            });
        }
        
        function generateEvaluationFile() {
            if (occupationId.value)
                axios.get(`/evaluationSkillFrameworks/generateEvaluationFile/${occupationId.value}`).then(response => {
                    evaluationFile.value.categories = response.data;
                });
        }
        
        function findById(id) {
            loading.value = true;
            axios.get(`/evaluationFiles/${id}`).then((response) => {
                if (props.duplicateAction) {
                    response.data.categories.forEach(element => {
                        let category = {
                            ...element,
                            id: null,
                            criteria: []
                        }
                        element.criteria.forEach(criterion => {
                            category.criteria.push({
                                ...criterion,
                                id: null
                            })
                        });
                        evaluationFile.value.categories.push(category)
                    });
                    response.data.questions.forEach(element => {
                        let question = {
                            ...element,
                            id: null,
                            selections: []
                        }
                        element.selections.forEach(selection => {
                            question.selections.push({
                                ...selection,
                                id: null
                            })
                        });
                        evaluationFile.value.questions.push(question);
                    });
                    evaluationFile.value.isPercent = response.data.isPercent;
                    evaluationFile.value.preSelectedNote = response.data.preSelectedNote;
                    evaluationFile.value.activateTarget = response.data.activateTarget;
                    evaluationFile.value.activateCategoryGravity = response.data.activateCategoryGravity;
                    evaluationFile.value.activateCriterionGravity = response.data.activateCriterionGravity;
                    evaluationFile.value.cycle = response.data.cycle;
                    evaluationFile.value.occupationNames = response.data.occupationNames;
                    evaluationFile.value.socioProfessionalIds = response.data.socioProfessionalIds;
                    evaluationFile.value.socioProfessionalNames = response.data.socioProfessionalNames;
                } else {
                    evaluationFile.value = response.data;
                }
                
                if (props.type === 'EVALUATION_SKILL' && evaluationFile.value.occupationIds.length > 0) {
                    occupationId.value = evaluationFile.value.occupationIds[0];
                }
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                evaluationFile.value.type = props.type;
                evaluationFile.value.questions = questions.value;
                evaluationFile.value.categories = categories.value;
                if (props.type === 'EVALUATION_SKILL')
                    evaluationFile.value.occupationIds = [occupationId.value];
                buildOrder();
                axios.post('/evaluationFiles', evaluationFile.value).then((response) => {
                    evaluationFileStore.addEvaluationFile(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                evaluationFile.value.type = props.type;
                evaluationFile.value.questions = questions.value;
                evaluationFile.value.categories = categories.value;
                if (props.type === 'EVALUATION_SKILL')
                    evaluationFile.value.occupationIds = [occupationId.value];
                buildOrder();
                axios.put('/evaluationFiles', evaluationFile.value).then((response) => {
                    if (props.archiveAction || props.unarchiveAction) {
                        evaluationFileStore.deleteEvaluationFile(evaluationFile.value);
                    } else {
                        evaluationFileStore.updateEvaluationFile(response.data);
                    }
                    
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function buildOrder() {
            evaluationFile.value.categories.forEach((category, index) => {
                category.order = index;
                category.criteria.forEach((criterion, index) => {
                    criterion.order = index;
                });
            });
            
            evaluationFile.value.questions.forEach((question, index) => {
                question.order = index;
                question.selections.forEach((selection, index) => {
                    selection.order = index;
                });
            });

            evaluationFile.value.steps.forEach((step, index) => {
                step.order = index;
            });
        }
        
        function remove() {
            loading.value = true;
            axios.delete(`/evaluationFiles/${evaluationFile.value.id}`).then(() => {
                evaluationFileStore.deleteEvaluationFile(evaluationFile.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function archive() {
            evaluationFile.value.archive = true;
            update();
        }
        
        function unarchive() {
            evaluationFile.value.archive = false;
            update();
        }


        onMounted(() => {
            if (props.addAction) {
                getDefaultValues();
            } else {
                findById($route.params.id);
            }
            
            findNotAssignedOccupation();
        });
        
        return {
            evaluationFileStore,
            mxRules,
            readonly,
            dialog,
            loading,
            overlay,
            evaluationFile,
            occupationId,
            loadingOccupations,
            occupations,
            evaluationCycles,
            questions,
            categories,
            generateEvaluationFile,
            save,
            update,
            remove,
            archive,
            unarchive,
            getBack,
        };
    }
}
</script>

<template>
    <div>
        <v-dialog
            v-model="dialog"
            max-width="1200"
            persistent
            scrollable
        >
            <v-card>
                <w-dialog-toolbar
                    :view-action="viewAction"
                    :delete-action="deleteAction"
                    :add-action="addAction || duplicateAction"
                    :update-action="updateAction"
                    :archive-action="archiveAction"
                    :unarchive-action="unarchiveAction"
               />
                <w-overlay
                    :overlay="overlay"
                    :loading="loading"
                    :delete-action="deleteAction"
                    :archive-action="archiveAction"
                    :unarchive-action="unarchiveAction"
                    @remove="remove"
                    @archive="archive"
                    @unarchive="unarchive"
                    @cancel="overlay = false"
                />
                <v-card-text>
                    <v-container grid-list-md>
                        <v-form ref="form">
                            <w-fieldset :title="$t('common.general')">
                                <v-row dense>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.code')" required/>
                                        <w-code-generation
                                            v-model="evaluationFile.code"
                                            entity="EVALUATION_FILE"
                                            :load="addAction || duplicateAction"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        ></w-code-generation>
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.label')" required/>
                                        <w-text-field
                                            v-model="evaluationFile.name"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col
                                        v-if="type === 'EVALUATION_PERFORMANCE' || type === 'EVALUATION_SKILL'"
                                        cols="6"
                                    >
                                        <w-label :label="$t('evaluation.cycle')"/>
                                        <w-enum-select
                                            v-model="evaluationFile.cycle"
                                            :items="evaluationCycles"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    
                                    
                                    <v-col :cols="type === 'EVALUATION_PERFORMANCE' || type === 'EVALUATION_SKILL' ? '6' : '12'">
                                        <w-label :label="$t('module.occupation')" required/>
                                        <w-enum-select
                                            v-if="type === 'EVALUATION_SKILL'"
                                            v-model="occupationId"
                                            :items="occupations"
                                            :loading="loadingOccupations"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                            @change="generateEvaluationFile"
                                        />
                                        <str-occupation-autocomplete
                                            v-else-if="type === 'EVALUATION_PERFORMANCE'"
                                            v-model="evaluationFile.occupationIds"
                                            :readonly="readonly"
                                            multiple
                                            :rules="[mxRules.listRequired]"
                                        />
                                        <str-occupation-autocomplete
                                            v-else
                                            v-model="evaluationFile.occupationIds"
                                            multiple
                                            external
                                            :external-items="occupations"
                                            :external-loading="loadingOccupations"
                                            :readonly="readonly"
                                            :rules="[mxRules.listRequired]"
                                        />
                                        <!--<w-enum-select
                                            v-else
                                            v-model="evaluationFile.occupationIds"
                                            :items="occupations"
                                            :loading="loadingOccupations"
                                            :readonly="readonly"
                                            multiple
                                            :rules="[mxRules.listRequired]"
                                        />-->
                                    </v-col>
                                    
                                    <!--<v-col cols="6">
                                        <w-label :label="$t('data_select.SOCIO_PROFESSIONAL')"/>
                                        <w-data-select-autocomplete
                                            v-model="evaluationFile.socioProfessionalIds"
                                            :readonly="readonly"
                                            multiple
                                            type="SOCIO_PROFESSIONAL"
                                        />
                                    </v-col>-->
                                    

                                </v-row>
                            </w-fieldset>
                            <br/>
                            <w-fieldset
                                v-if="type === 'EVALUATION_PERFORMANCE'"
                                :title="$t('evaluation.pay_gateway')"
                            >
                                <v-col cols="12">
                                    <w-label :label="$t('evaluation.set_pay_section')" required/>
                                    <text-field-pay-section
                                        v-model="evaluationFile.gateway"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </w-fieldset>
                            <br/>
                            <criteria-form
                                v-model="categories"
                                :init-categories="evaluationFile.categories"
                                :type="type"
                                :activate-category-gravity="evaluationFile.activateCategoryGravity"
                                :activate-criterion-gravity="evaluationFile.activateCriterionGravity"
                                :activate-target="evaluationFile.activateTarget"
                                :preSelectedNote="evaluationFile.preSelectedNote"
                                :readonly="readonly"
                            />
                            <br/>
                            <question-form
                                v-model="questions"
                                :type="type"
                                :init-questions="evaluationFile.questions"
                                :readonly="readonly"
                            />
                            <br/>
                            <w-fieldset
                                v-if="type === 'EVALUATION_SKILL' || type === 'EVALUATION_PERFORMANCE'"
                                :title="$t('common.steps')"
                                show-add-action
                                @add="evaluationFile.steps.push({})"
                            >
                                <v-row
                                    v-for="(step, index) in evaluationFile.steps"
                                    :key="`step-${index}`"
                                    dense
                                >
                                    <v-col cols="11">
                                        <w-label :label="$t('common.step')" required/>
                                        <str-evaluation-phase-autocomplete
                                            v-model="step.phaseId"
                                            :type="type"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="1">
                                        <w-btn-icon
                                            color="red"
                                            icon="mdi-trash-can"
                                            classe="mt-6"
                                            :disabled="readonly"
                                            @press="evaluationFile.steps.splice(index, 1)"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                            <br/>
                            <w-fieldset :title="$t('common.comment')">
                                <w-simple-text-area
                                    v-model="evaluationFile.description"
                                    :readonly="readonly"
                                />
                            </w-fieldset>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="getBack"/>
                    <w-btn-add
                        :show="addAction || duplicateAction"
                        :loading="loading"
                        @press="save(false)"
                    />
                    <w-btn-update
                        :show="updateAction"
                        :loading="loading"
                        @press="update"
                    />
                    <w-btn-delete
                        :show="deleteAction"
                        :loading="loading"
                        @press="overlay = true"
                    />
                    <w-btn-archive
                        :show="archiveAction"
                        :loading="loading"
                        @press="overlay = true"
                    />
                    <w-btn-unarchive
                        :show="unarchiveAction"
                        :loading="loading"
                        @press="overlay = true"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
        
        <router-view/>
    </div>
</template>